import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, UploadCloud, Globe, ScanSearch, Check, X, Loader2, ImageIcon, Wand2 } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { recognizeProduct, type RecognitionResult } from '../lib/productRecognition';
interface SmartDropEngineProps {
  /** Called when the admin accepts a recognised draft to prefill the drop form. */
  onApply: (result: RecognitionResult) => void;
}
type Phase = 'idle' | 'scanning' | 'result';

/**
 * Smart Drop Engine — upload a product photo + its website, "recognise" the
 * product and brand, and turn it into a ready-to-edit drop draft. Recognition
 * runs locally (no external LLM); a short staged progress makes the analysis
 * legible to the admin.
 */
export function SmartDropEngine({
  onApply
}: SmartDropEngineProps) {
  const [image, setImage] = useState<string | null>(null);
  const [website, setWebsite] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<RecognitionResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const readFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setImage(String(reader.result));
    reader.readAsDataURL(file);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readFile(file);
  };
  const canScan = !!image || website.trim().length > 0;
  const runRecognition = () => {
    if (!canScan) return;
    setPhase('scanning');
    setProgress(0);
    // Staged, legible "analysis" — the recognition itself is instant & local.
    const steps = [18, 42, 70, 92, 100];
    let i = 0;
    const tick = () => {
      setProgress(steps[i]);
      i += 1;
      if (i < steps.length) {
        window.setTimeout(tick, 260);
      } else {
        const r = recognizeProduct({
          imageDataUrl: image || undefined,
          website
        });
        setResult(r);
        window.setTimeout(() => setPhase('result'), 300);
      }
    };
    tick();
  };
  const reset = () => {
    setImage(null);
    setWebsite('');
    setResult(null);
    setProgress(0);
    setPhase('idle');
  };
  const confColor = (c: number) => c >= 75 ? 'text-green-400 bg-green-500/15' : c >= 45 ? 'text-brand-yellow bg-brand-yellow/15' : 'text-red-400 bg-red-500/15';
  return <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-id="element-674" data-mp="1j3vr6o6mag2g">
      <div className="flex items-start gap-3 mb-4" data-id="element-675" data-mp="1e9rkm619ka1w">
        <div className="w-10 h-10 rounded-lg bg-brand-blue/20 flex items-center justify-center shrink-0" data-id="element-676" data-mp="qlhz757ypoba">
          <Wand2 className="w-5 h-5 text-brand-blue" data-id="element-677" data-mp="ln6t95ksfxyc" />
        </div>
        <div data-id="element-678" data-mp="lkubasf15nla">
          <h3 className="font-bold text-white text-lg flex items-center gap-2" data-id="element-679" data-mp="2xaaaqe1ikmok">
            Smart Drop Engine
            <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-blue/20 text-brand-blue px-2 py-0.5 rounded-full" data-id="element-680" data-mp="321a7njamv4r2">
              Beta
            </span>
          </h3>
          <p className="text-sm text-gray-400" data-id="element-681" data-mp="2sanx43hrx4f8">
            Upload a product photo and its website — the engine recognises the
            product &amp; brand and drafts the drop for you.
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait" data-id="element-682" data-mp="3kyjrv2ns8m9d">
        {/* ---------------- INPUT ---------------- */}
        {phase === 'idle' && <motion.div key="idle" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-683" data-mp="36zbnlvcrinyb">
            {/* Image upload */}
            <div data-id="element-684" data-mp="20katzwb7mhuw">
              <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-685" data-mp="20ksz7ifuo6ey">
                Product photo
              </label>
              <div onDragOver={e => {
            e.preventDefault();
            setDragging(true);
          }} onDragLeave={() => setDragging(false)} onDrop={onDrop} onClick={() => fileRef.current?.click()} role="button" tabIndex={0} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && fileRef.current?.click()} className={`relative flex flex-col items-center justify-center h-44 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${dragging ? 'border-brand-blue bg-brand-blue/10' : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'}`} data-id="element-686" data-mp="2fv93sakhjml7">
                {image ? <>
                    <img src={image} alt="Uploaded product" className="max-h-full max-w-full object-contain p-2" data-id="element-687" data-mp="3l0o3vfsju7r2" />
                    <button type="button" onClick={e => {
                e.stopPropagation();
                setImage(null);
              }} className="absolute top-2 right-2 p-1.5 bg-gray-900/80 hover:bg-gray-900 rounded-full text-gray-300" aria-label="Remove image" data-id="element-688" data-mp="3u1dxccuc7m1m">
                      <X className="w-4 h-4" data-id="element-689" data-mp="hk8ldbugav4g" />
                    </button>
                  </> : <>
                    <UploadCloud className="w-8 h-8 text-gray-500 mb-2" data-id="element-690" data-mp="lk9c9vyqkyse" />
                    <p className="text-sm text-gray-400 font-medium" data-id="element-691" data-mp="2jbu0tm52jw8j">
                      Drop an image or click to upload
                    </p>
                    <p className="text-[11px] text-gray-600 mt-1" data-id="element-692" data-mp="2nd79e3zjqdj5">
                      PNG / JPG of the product
                    </p>
                  </>}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) readFile(file);
            }} data-id="element-693" data-mp="3lq1ovnmxbg9g" />
              </div>
            </div>

            {/* Website + action */}
            <div className="flex flex-col" data-id="element-694" data-mp="8hf0wd5qoicm">
              <label className="text-xs text-gray-500 uppercase font-bold mb-1.5 block" data-id="element-695" data-mp="n74ifiy52yz2">
                Product website
              </label>
              <div className="relative" data-id="element-696" data-mp="i92t4ga4e3f5">
                <Globe className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-697" data-mp="iadts789qv21" />
                <Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="e.g. vitaminwell.com/energy" className="bg-gray-800 border-gray-700 text-white pl-9" data-id="element-698" data-mp="w5g73ffg3mov" />
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5" data-id="element-699" data-mp="1fri18o6t8b8e">
                The domain is the strongest signal for the brand. A path (e.g.
                /energy) helps guess the product type.
              </p>
              <div className="flex-1" data-id="element-700" data-mp="s4074m02p129" />
              <Button type="button" onClick={runRecognition} disabled={!canScan} className="mt-3 flex items-center justify-center gap-2 disabled:opacity-50 text-base py-3" data-id="element-701" data-mp="s2yu4bzhy4hw">
                <ScanSearch className="w-5 h-5" data-id="element-702" data-mp="i3zkaq2zw2tb" />
                Recognise product
              </Button>
            </div>
          </motion.div>}

        {/* ---------------- SCANNING ---------------- */}
        {phase === 'scanning' && <motion.div key="scanning" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="flex flex-col items-center justify-center py-10 text-center" data-id="element-703" data-mp="e37cm19ru5z5">
            <div className="relative w-24 h-24 mb-5" data-id="element-704" data-mp="jo2j9mi7ogde">
              {image ? <img src={image} alt="" aria-hidden="true" className="w-full h-full object-contain rounded-xl opacity-70" data-id="element-705" data-mp="jq5i8w1cdju9" /> : <div className="w-full h-full rounded-xl bg-gray-800 flex items-center justify-center" data-id="element-706" data-mp="5wxnivhys88c">
                  <ImageIcon className="w-8 h-8 text-gray-600" data-id="element-707" data-mp="x1nf7ce10y2" />
                </div>}
              <motion.span aria-hidden="true" className="absolute left-0 right-0 h-0.5 bg-brand-blue shadow-[0_0_12px_2px_rgba(27,79,196,0.7)]" initial={{
            top: 0
          }} animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 1.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-708" data-mp="3s4e31hxmh6bv" />
            </div>
            <p className="text-white font-bold flex items-center gap-2" data-id="element-709" data-mp="23jzya7vquvb6">
              <Loader2 className="w-4 h-4 animate-spin text-brand-blue" data-id="element-710" data-mp="23mckwycg5tuk" />
              Analysing product &amp; brand…
            </p>
            <div className="w-full max-w-xs h-2 rounded-full bg-gray-800 overflow-hidden mt-4" data-id="element-711" data-mp="2clg6qa300ncr">
              <motion.div className="h-full bg-brand-blue rounded-full" animate={{
            width: `${progress}%`
          }} transition={{
            ease: 'easeOut'
          }} data-id="element-712" data-mp="1fv78kt5ptjp0" />
            </div>
          </motion.div>}

        {/* ---------------- RESULT ---------------- */}
        {phase === 'result' && result && <motion.div key="result" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} data-id="element-713" data-mp="1tndgjrypskdd">
            <div className="flex items-start gap-4 rounded-xl bg-gray-800/60 border border-gray-700 p-4" data-id="element-714" data-mp="1146plu7ce7em">
              <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center p-1.5 shrink-0" data-id="element-715" data-mp="5qtllpxktq9p">
                <img src={result.brandLogo} alt={result.brand} className="max-w-full max-h-full object-contain" data-id="element-716" data-mp="9v195jsat06c" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-717" data-mp="jp1ki3e6b1ni">
                <div className="flex items-center gap-2 flex-wrap" data-id="element-718" data-mp="3i9jc573dtpj3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-brand-blue" data-id="element-719" data-mp="3n7ulzhtog01b">
                    Recognised
                  </span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${confColor(result.confidence)}`} data-id="element-720" data-mp="3s5cwhcncenbf">
                    {result.confidence}% match
                  </span>
                </div>
                <h4 className="text-white font-bold text-lg leading-tight mt-0.5" data-id="element-721" data-mp="34h3eklt92jpk">
                  {result.brand}
                </h4>
                <p className="text-sm text-gray-300" data-id="element-722" data-mp="2wwj26uz4hhag">{result.title}</p>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2" data-id="element-723" data-mp="2ry7t4mgnjata">
                  {result.description}
                </p>
              </div>
            </div>

            {/* Signals */}
            <ul className="mt-3 space-y-1.5" data-id="element-724" data-mp="2i0tkva4vtsc2">
              {result.signals.map((s, i) => <li key={i} className="flex items-start gap-2 text-xs text-gray-400" data-id="element-725" data-mp="2dyq0fw08jden" data-mpi={i}>
                  <Sparkles className="w-3.5 h-3.5 text-brand-yellow shrink-0 mt-0.5" data-id="element-726" data-mp="297hr0zywns1h" />
                  {s}
                </li>)}
            </ul>

            {/* Draft summary chips */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs" data-id="element-727" data-mp="1zbn1rm06eypq">
              <DraftChip label="Campaign" value={result.campaign} data-id="element-728" data-mp="1u9xn7wdqw2ww" />
              <DraftChip label="Products" value={`${result.products.length} item`} data-id="element-729" data-mp="1q9nkcsl91sm9" />
              <DraftChip label="Questions" value={`${result.questions.length} added`} data-id="element-730" data-mp="1fn8p9yz55xsm" />
              <DraftChip label="Website" value={result.websiteUrl} data-id="element-731" data-mp="1koo9xpxeq2ah" />
              <DraftChip label="Instagram" value={result.instagramUrl} data-id="element-732" data-mp="16on9gbl20wvj" />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-5" data-id="element-733" data-mp="1bnq5x7myxurw">
              <Button type="button" onClick={() => onApply(result)} className="flex-1 flex items-center justify-center gap-2" data-id="element-734" data-mp="1zcj65gamiosb">
                <Check className="w-5 h-5" data-id="element-735" data-mp="1pmhi2oi9t6de" />
                Use this to create drop
              </Button>
              <Button type="button" variant="secondary" onClick={reset} className="sm:w-auto" data-id="element-736" data-mp="1ulawcj5dt1em">
                Start over
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function DraftChip({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 min-w-0" data-id="element-737" data-mp="2sf22u16hywiu">
      <span className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold" data-id="element-738" data-mp="2sd95xdlorqo1">
        {label}
      </span>
      <span className="block text-gray-200 truncate" data-id="element-739" data-mp="317nmrklv7999">{value}</span>
    </div>;
}